import ko from "knockout";
import viewHtml from "./hsl-attention-signal-icon.html";
import { MaybeObservable, MaybeObservableArray } from "../interfaces/MaybeObservable";
import moment from "moment";
//import uniqueId from "framework/helpers/uniqueId";
import uniqueId from "flow/utils/uniqueId";
//import dateHelper from "framework/helpers/dateHelper";
import dateHelper from "flow/utils/dateHelper";

// This is the component for the attention signal icon used in HSL.
export enum HslAttentionSignalSeverityDegreeType {
    None = 0,
    Troublesome = 1,
    Harmful = 2,
    LifeThreatening = 3
}
export enum HslAttentionSignalInformationType {
    None = 0,
    Hypersensitivity = 1,
    MedicalConditionAndTreatment = 2,
    CareProcedureDeviation = 3,
    NonStructuredAttentionInformation = 4
}
export enum HslAttentionSignalHeaderType {
    Warning = 0,
    Infection = 1,
    Observe = 2
}

export interface IHslAttentionSignal {
    endDate: MaybeObservable<string> | MaybeObservable<moment.Moment>;
    headerType: MaybeObservable<HslAttentionSignalHeaderType>;
    informationType: MaybeObservable<HslAttentionSignalInformationType>;
    severityDegree: MaybeObservable<HslAttentionSignalSeverityDegreeType>;
}

export interface IHslAttentionSignalIconParams {
    attentionSignals: MaybeObservableArray<IHslAttentionSignal>;
}

export default class HslAttentionSignalIconViewModel {
    params: IHslAttentionSignalIconParams;
    componentId = uniqueId("hsl-attention-signal-icon-");

    constructor(params: IHslAttentionSignalIconParams) {
        this.params = params;
    }

    fillBottomRightColour = ko.pureComputed((): boolean => {
        const attentionSignals = this.getOngoingAttentionSignalsExcludingInfection();
        return attentionSignals.some(
            attentionSignal =>
                ko.unwrap(attentionSignal.informationType) === HslAttentionSignalInformationType.CareProcedureDeviation
        );
    });

    fillTopRightColour = ko.pureComputed((): boolean => {
        const attentionSignals = this.getOngoingAttentionSignalsExcludingInfection();
        return attentionSignals.some(
            attentionSignal =>
                ko.unwrap(attentionSignal.informationType) ===
                HslAttentionSignalInformationType.MedicalConditionAndTreatment
        );
    });

    fillBottomLeftColour = ko.pureComputed((): boolean => {
        const attentionSignals = this.getOngoingAttentionSignals();
        return attentionSignals.some(
            attentionSignal => ko.unwrap(attentionSignal.headerType) === HslAttentionSignalHeaderType.Infection
        );
    });

    fillTopLeftColour = ko.pureComputed((): boolean => {
        const attentionSignals = this.getOngoingAttentionSignalsExcludingInfection();
        const as =
            attentionSignals.some(
                attentionSignal =>
                    ko.unwrap(attentionSignal.informationType) ===
                    HslAttentionSignalInformationType.NonStructuredAttentionInformation
            ) ||
            attentionSignals.some(
                attentionSignal => ko.unwrap(attentionSignal.informationType) === HslAttentionSignalInformationType.None
            );
        return as;
    });

    fillTopColour = ko.pureComputed((): boolean => {
        const attentionSignals = this.getOngoingAttentionSignalsExcludingInfection();
        const as = attentionSignals.some(
            attentionSignal =>
                ko.unwrap(attentionSignal.informationType) === HslAttentionSignalInformationType.Hypersensitivity &&
                (ko.unwrap(attentionSignal.severityDegree) === HslAttentionSignalSeverityDegreeType.None ||
                    ko.unwrap(attentionSignal.severityDegree) === HslAttentionSignalSeverityDegreeType.LifeThreatening)
        );

        return as;
    });

    fillCenterColour = ko.pureComputed((): boolean => {
        const attentionSignals = this.getOngoingAttentionSignalsExcludingInfection();
        return attentionSignals.some(
            attentionSignal =>
                ko.unwrap(attentionSignal.informationType) === HslAttentionSignalInformationType.Hypersensitivity &&
                ko.unwrap(attentionSignal.severityDegree) !== HslAttentionSignalSeverityDegreeType.Troublesome
        );
    });

    fillBottomColour = ko.pureComputed((): boolean => {
        const attentionSignals = this.getOngoingAttentionSignalsExcludingInfection();
        return attentionSignals.some(
            attentionSignal =>
                ko.unwrap(attentionSignal.informationType) === HslAttentionSignalInformationType.Hypersensitivity
        );
    });

    getOngoingAttentionSignals = ko.pureComputed((): IHslAttentionSignal[] => {
        let attentionSignals: IHslAttentionSignal[] = ko.unwrap<IHslAttentionSignal[]>(
            this.params?.attentionSignals ?? []
        );
        if (!attentionSignals.length) {
            return attentionSignals;
        }

        const dateToday = moment(new Date()).startOf("day");

        attentionSignals = attentionSignals.filter(attentionSignal => {
            const endDate = ko.unwrap(attentionSignal.endDate);
            if (!endDate) {
                return true;
            }

            const endDateMoment =
                typeof endDate === "string" ? moment(dateHelper.toLocalDateFormat(endDate, false)) : endDate;

            return endDateMoment.isSameOrAfter(dateToday);
        });

        return attentionSignals;
    });

    getOngoingAttentionSignalsExcludingInfection = ko.pureComputed((): IHslAttentionSignal[] => {
        let attentionSignals = this.getOngoingAttentionSignals();

        attentionSignals = attentionSignals.filter(
            attentionSignal => ko.unwrap(attentionSignal.headerType) !== HslAttentionSignalHeaderType.Infection
        );

        return attentionSignals;
    });
}

ko.components.register("hsl-attention-signal-icon", {
    viewModel: HslAttentionSignalIconViewModel,
    template: viewHtml
});
